import { isResourceDeleting } from '../../utils/resourceLifecycle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useNow from '../../hooks/useNow';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Alert,
  Divider,
  Tooltip,
  Stack,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useAuth } from '../../contexts/AuthContext';
import { useAttributeOptions } from '../../contexts/AttributesContext';
import { authenticatedFetch } from '../../services/api_utils';
import { functionEndpoint } from '../../services/endpoints';
import {
  doc,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  isPublishedVersion,
  loadFunctionVersion,
  loadRunTarget,
  prepareHistoricalRunParameters,
  versionBlockNames,
  loadVersionBlockNames,
  versionDataSourceBlocks,
  versionEmailOutputs,
  attachmentLabel,
  versionHasExternalEffect,
} from '../../services/functionVersions';
import { loadLatestCompletedRunsFor, watchCardRuns } from '../../services/functionRuns';
import { formatRunTime, runCompletedAt, runCreatedAt } from '../../utils/runDisplay';
import { isRunSettled } from '../../utils/runProjection';
import {
  compareParameters,
  formatParameterInputValue,
  isDateRangeParam,
  prepareParameterControls,
  formatParameterErrors,
  parameterCompatibilityErrors,
  describeContractChanges,
  resolveParameterType,
  emptyParameterValue,
  sameRunParameters,
  isEmptyParameterValue,
  filterSignature,
  hiddenDisagreements,
  findInvertedDateRange,
} from '../../utils/functionParameterUtils';
import { isExternalTable } from '../../utils/tableTypes';
import {
  GRID_COLS,
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
  resolveViewModel,
  widgetInstanceIds,
  viewContentSignature,
  withDisplayConstraints,
} from '../../utils/widgetModel';
import WidgetCard from './widgets/WidgetCard';
import WidgetDisplayEditor from './WidgetDisplayEditor';
import KeepViewCurrentPanel from './KeepViewCurrentPanel';
import { cardLabels, controlsFromRunParameters, runBelongsToCard } from '../../utils/viewCards';
import ParameterField from '../common/ParameterField';
import DateDraftScope, { INCOMPLETE_DATES_MESSAGE } from '../common/DateDraftScope';
import LoadFailure, { loadFailureMessage } from '../common/LoadFailure';
import { list as listContacts } from '../../services/emailContacts';
import { blockedRecipients, confirmationChanged, recipientEntry } from '../../utils/emailConfirmation';
import { isEmailFeatureEnabled } from '../../utils/outputRegistry';
import { formatText } from '../common/TextFormatterProvider';
import useRunResultHydration from '../../hooks/useRunResultHydration';

// Run the public run_data_function endpoint with the explicit request envelope
// (section 3.11). The backend no longer accepts flat parameters. Error responses
// carry a machine `code`, so the HTTP status/code are attached to the thrown
// error (428 external_effect_confirmation_required re-surfaces the confirmation).
// (runFunctionEndpoint, below.)

// A run clicked between a publish and the rebuild of the card's controls.
const CONTRACT_CHANGED_MESSAGE = 'This function was republished while this view was open. Its filters are being refreshed — review them and run again.';

const DATA_SOURCES_CHANGED_MESSAGE = "This view's member functions were republished with changes to their data sources.";

// The notice a card shows after its Function is published while the View is
// open. `differences` is what parameterCompatibilityErrors found between the
// version the card's controls were built from and the newly published one.
// A head change that published nothing new (a validity flag flipping back)
// has nothing to announce.
const publishNotice = (previousVersion, nextVersion, differences) => {
  if (previousVersion === nextVersion) return null;
  if (!previousVersion) {
    return {
      message: `This function was published (v${nextVersion}) while this view was open. Its filters are now available.`,
      changes: [],
    };
  }
  if (differences.length === 0) {
    return {
      message: `This function was republished (v${previousVersion} → v${nextVersion}) while this view was open.`,
      changes: [],
    };
  }
  return {
    message: `${DATA_SOURCES_CHANGED_MESSAGE} This card's filters were reset to the new defaults.`,
    changes: describeContractChanges(differences),
  };
};

// The notice a card shows when the run on screen cannot lend its parameters to
// the sidebar: `errors` is why prepareHistoricalRunParameters refused them.
// A difference between the run's version and the published one carries a
// `kind` and is listed as a change; any other refusal (an unreadable version,
// a recorded value the current filters reject) is a reason, stated in the
// message itself.
const historicalRunNotice = (run, errors, dataSourceBlocks) => {
  const dataSourcesChanged = errors.some((error) => error.kind);
  if (dataSourcesChanged) {
    return {
      message: `${DATA_SOURCES_CHANGED_MESSAGE} This result was produced by version ${run.function_version}, so the filters shown may differ from the ones it used.`,
      changes: describeContractChanges(errors),
    };
  }
  const refusal = formatParameterErrors(errors, dataSourceBlocks).split('\n').join(' ');
  return {
    message: `This result's filters could not be shown in the sidebar, so the filters shown may differ from the ones it used. ${refusal}`,
    changes: [],
  };
};

// Why an instance cannot run. Runnable means: published, and not invalidated by a
// missing dependency.
const instanceBlockReason = (func) => {
  if (!func) return "This card's function was deleted";
  if (isResourceDeleting(func)) return 'This Function is being deleted.';
  if (func.invalid) return func.invalid_reason || 'This function references a deleted resource';
  if (!isPublishedVersion(func.publishedVersion ?? func.published_version)) {
    return 'Publish this function to run it';
  }
  return '';
};

// One instance's controls, built from a version's data-source contract. With no
// previous controls every filter starts from the version's default. With
// previous controls (the values on screen before a rebuild, or `{}` for Clear)
// a filter keeps its previous value and a filter they omit stays empty. Range
// and reporting-date filters have no IS/NOT operator.
const buildInstanceControls = (dataSourceBlocks, previousControls = null) => {
  const functionParams = {};
  const excludedColumns = {};
  const tableIds = [];
  Object.entries(dataSourceBlocks || {}).forEach(([blockId, block]) => {
    const blockParams = {};
    const previousBlock = previousControls?.[blockId]?.data || {};
    (block.filters || []).forEach((filter) => {
      if (!filter.variable) return;
      const declaredType = resolveParameterType(filter.column, filter.type);
      const valArr = Array.isArray(filter.value) ? filter.value : [];
      let fallback = emptyParameterValue(filter);
      if (previousControls === null) {
        if (filter.range) {
          fallback = { gte: filter.range.gte || '', lte: filter.range.lte || '' };
        } else if (isDateRangeParam(filter.column)) {
          fallback = formatParameterInputValue(valArr[0] ?? '', declaredType, { multi: false, dateRange: true });
        } else {
          fallback = valArr;
        }
      }
      blockParams[filter.column] = Object.prototype.hasOwnProperty.call(previousBlock, filter.column)
        ? previousBlock[filter.column]
        : fallback;
      // Keyed by block AND column: exclusion is a per-source choice.
      if (!filter.range && !isDateRangeParam(filter.column) && previousControls?.[blockId]?.excluded?.[filter.column]) {
        excludedColumns[`${blockId}::${filter.column}`] = true;
      }
    });
    if (Object.keys(blockParams).length > 0) {
      functionParams[blockId] = {
        name: block.name || blockId,
        data: blockParams,
        tableId: block.table_id,
      };
    }
    if (block.table_id) tableIds.push(block.table_id);
  });
  return { functionParams, excludedColumns, tableIds };
};

// Copy a card's local values and IS/NOT choices into the `previousControls`
// shape buildInstanceControls reads, when rebuilding after a Function publish.
const foldExcluded = (instanceId, funcParams, excluded) => {
  const folded = {};
  Object.entries(funcParams || {}).forEach(([blockId, blockObj]) => {
    const blockExcluded = {};
    Object.keys(blockObj?.data || {}).forEach((column) => {
      if (excluded?.[`${instanceId}::${blockId}::${column}`]) blockExcluded[column] = true;
    });
    folded[blockId] = Object.keys(blockExcluded).length > 0
      ? { ...blockObj, excluded: blockExcluded }
      : blockObj;
  });
  return folded;
};

const fetchTableSchemas = async (companyName, tableIds) => {
  const schemas = {};
  for (const tableId of tableIds) {
    try {
      const tableDoc = await getDoc(doc(db, `users/${companyName}/tables`, tableId));
      if (tableDoc.exists()) schemas[tableId] = tableDoc.data();
    } catch (error) {
      console.error(`Error fetching table ${tableId}:`, error);
    }
  }
  return schemas;
};

// The attribute (tag) columns a contract's variable filters read, whose value
// lists feed the parameter pickers.
const attributeColumnsFor = (dataSourceBlocks, schemas) => {
  const columns = new Set();
  Object.values(dataSourceBlocks || {}).forEach((block) => {
    const tableSchema = block.table_id ? schemas[block.table_id] : null;
    if (!tableSchema) return;
    const attributeColumns = [...(tableSchema.attributeColumns || []), ...(tableSchema.tableAttributeColumns || [])];
    (block.filters || []).forEach((filter) => {
      if (filter.variable && attributeColumns.some((column) => column.name === filter.column)) {
        columns.add(filter.column);
      }
    });
  });
  return Array.from(columns);
};

async function runFunctionEndpoint(envelope) {
  const response = await authenticatedFetch(functionEndpoint('run_data_function'), {
    method: 'POST',
    body: JSON.stringify(envelope)
  });

  if (!response.ok) {
    const text = await response.text();
    let payload = null;
    try { payload = text ? JSON.parse(text) : null; } catch (parseErr) { payload = null; }
    const message = (payload && (payload.message || payload.error)) || text || `Request failed with status ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.code = payload && payload.code ? payload.code : null;
    throw error;
  }

  return response.json();
}

// One run document mapped into what the widgets read. Both the first hydration
// and the live repaint below go through this, so a card repainted by a trigger
// is built exactly like one painted on load: the run's bounded `result_preview`
// paints immediately and the generation-guarded fetch upgrades it to the
// complete stored result.
const hydrateFromRun = (run) => ({
  runId: run.id,
  outputs: run.result_preview?.outputs && typeof run.result_preview.outputs === 'object'
    ? run.result_preview.outputs
    : {},
  truncatedTables: Array.isArray(run.result_preview?.truncated_tables)
    ? run.result_preview.truncated_tables
    : [],
  outputsSummary: run.outputs_summary || null,
  resultGeneration: null,
  fullLoaded: false,
});

function ViewDisplay({ viewData, functions, latestView, onReload }) {
  const { companyName, currentUser } = useAuth();
  const savedSignature = useMemo(() => viewContentSignature(viewData), [viewData]);
  const latestSignature = useMemo(() => viewContentSignature(latestView), [latestView]);
  const viewChanged = latestView !== undefined && savedSignature !== latestSignature;
  // Relative run times ("just now", "Yest") follow the shared clock.
  useNow();
  const [parameters, setParameters] = useState({});
  const [instanceResults, setInstanceResults] = useState({});
  const [isLoading, setIsLoading] = useState({});
  // Instances gated between a Run / Run All click and the moment their run
  // starts (executeInstance sets isLoading) or the gesture ends without running
  // them. captureRunTarget's version reads are awaited BEFORE executeInstance
  // sets isLoading, so the first click must lock the buttons here,
  // synchronously: a second click inside that window would otherwise send every
  // instance twice. The ref is the truth the handlers check; the state mirrors
  // it for the buttons.
  const preparingRef = useRef(new Set());
  const [preparing, setPreparing] = useState({});
  const [errors, setErrors] = useState({});
  // Per card: `{ message, changes }` about the version the card runs, shown as
  // a dismissible banner ABOVE the card's result. A notice never blocks a run
  // and never replaces the result; `errors` is for a card that cannot show one.
  const [versionNotices, setVersionNotices] = useState({});
  const [hydrating, setHydrating] = useState({});
  // Reads this view depends on that failed: the initial load (Retry re-runs it via
  // loadAttempt) and the recent-runs stream (Retry resubscribes via streamAttempt).
  // Per-instance read failures go to `errors[instanceId]` on the card instead.
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [streamError, setStreamError] = useState(false);
  const [streamAttempt, setStreamAttempt] = useState(0);
  const [tableSchemas, setTableSchemas] = useState({}); // Store table schemas for data source blocks
  // The attribute columns the cards' parameters read; their value lists come
  // live from the AttributesProvider.
  const [parameterAttributeColumns, setParameterAttributeColumns] = useState([]);
  const attributeValues = useAttributeOptions(parameterAttributeColumns);
  const [functionsMap, setFunctionsMap] = useState({}); // Store the functions map with detailed block data
  const [excludedParams, setExcludedParams] = useState({});
  // Half-typed dates (see AppDatePicker): one scope per instance card, or one
  // for the unified controls, so a draft refuses only the runs it would feed.
  // Clear remounts the controls to discard drafts.
  const UNIFIED_DATE_SCOPE = 'unified';
  const dateScopeRefs = useRef({});
  const [dateDrafts, setDateDrafts] = useState({});
  const [parameterClearGeneration, setParameterClearGeneration] = useState(0);
  const setDateDraft = (scopeKey, partial) => {
    setDateDrafts((prev) => (Boolean(prev[scopeKey]) === partial ? prev : { ...prev, [scopeKey]: partial }));
  };
  const dateScopeProps = (scopeKey) => ({
    ref: (scope) => { dateScopeRefs.current[scopeKey] = scope; },
    onPartialChange: (partial) => setDateDraft(scopeKey, partial),
  });
  // In unified mode a card's dates live in the unified scope AND, for columns
  // the cards disagree on, in the card's own scope; both are revealed.
  // Every runnable card's own panel: its parameter controls and its Run button.
  // A card with no variable filters has no controls but still runs on its own.
  // `onlyColumns` narrows the panels to the columns the unified list could not
  // merge (see unifiedModel), so a card holding none of them has no panel there.
  const renderInstanceControls = (onlyColumns = null) => (
    runnableInstanceIds.map((instanceId) => {
                      const func = functionsMap[originalFunctionId(instanceId)];
                      const funcParams = parameters[instanceId] || {};
                      const hasControls = Object.keys(funcParams).length > 0;
                      if (onlyColumns && !Object.values(funcParams).some((blockObj) => Object.keys(blockObj?.data || {}).some((col) => onlyColumns.has(col)))) return null;

                      return (
                        <DateDraftScope key={instanceId} {...dateScopeProps(instanceId)}>
                        <Box
                          sx={{ mb: 3, border: (theme) => `1px solid ${theme.palette.divider}`, p: 2, borderRadius: 1 }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                            {func.name || func.id}
                          </Typography>
                          <Divider sx={{ mb: 2 }} />

                          {errors[instanceId] && (
                            <Alert severity="warning" sx={{ mb: 1.5 }}>{errors[instanceId]}</Alert>
                          )}

                          {!hasControls && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              This card has no configurable parameters.
                            </Typography>
                          )}

                          {Object.entries(funcParams).map(([blockId, blockObj]) => {
                            // A block the published version no longer declares has
                            // no contract to edit against.
                            const block = func.data_source_blocks?.[blockId];
                            if (!block) return null;
                            if (onlyColumns && !Object.keys(blockObj.data || {}).some((col) => onlyColumns.has(col))) return null;

                            const blockFilters = block.filters || [];
                            const tableId = block.table_id;
                            const tableSchema = tableSchemas[tableId];

                            return (
                              <Box key={blockId} sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                  {blockObj.name}
                                </Typography>

                                {Object.entries(blockObj.data).filter(([col]) => !onlyColumns || onlyColumns.has(col)).map(([col, value]) => {
                                  const thisFilter = blockFilters.find(f => f.column === col);
                                  const declaredType = resolveParameterType(col, thisFilter?.type);
                                  const isAttributeColumn = Boolean(
                                    tableSchema?.attributeColumns?.some(mc => mc.name === col)
                                    || tableSchema?.tableAttributeColumns?.some(mc => mc.name === col)
                                  );

                                  return (
                                    <ParameterField
                                      key={col}
                                      name={col}
                                      error={preparedParameters[instanceId]?.errors.find((issue) => (
                                        issue.blockId === blockId && issue.column === col
                                      ))?.message || ''}
                                      value={value}
                                      onChange={(next) => handleParameterChange(instanceId, blockId, col, next)}
                                      type={declaredType}
                                      isRange={Boolean(thisFilter?.range)}
                                      isDateRange={isDateRangeParam(col)}
                                      isAttribute={isAttributeColumn}
                                      availableValues={attributeValues[col]}
                                      indexedTableIds={indexedTableIdsFor(col, declaredType, tableId ? [tableId] : [])}
                                      disabled={!thisFilter?.variable || Boolean(hydrating[instanceId])}
                                      labelVariant="caption"
                                      excluded={Boolean(excludedParams[`${instanceId}::${blockId}::${col}`])}
                                      onToggleExclude={() => handleToggleExclude(instanceId, blockId, col)}
                                    />
                                  );
                                })}
                              </Box>
                            );
                          })}

                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={() => handleRunFunction(instanceId)}
                            disabled={Boolean(isLoading[instanceId] || preparing[instanceId])
                              || Boolean(confirmState) || Boolean(hydrating[instanceId])
                              || isResourceDeleting(func)}
                            sx={{ mt: 1 }}
                          >
                            {isLoading[instanceId] || preparing[instanceId] ? 'Running...' : 'Run'}
                          </Button>
                        </Box>
                        </DateDraftScope>
                      );
                    })
  );

  const revealDateDrafts = (instanceId) => (
    (unifyParameters ? [UNIFIED_DATE_SCOPE, instanceId] : [instanceId])
      .map((scope) => Boolean(dateScopeRefs.current[scope]?.reveal()))
      .some(Boolean)
  );
  // A start after its end in any of the card's blocks refuses its run (M41).
  const invertedInstanceDates = (instanceId) => (
    Object.values(parameters[instanceId] || {}).map((block) => findInvertedDateRange(block?.data)).find(Boolean) || null
  );
  // Pending email effect confirmation. null when no confirmation is open;
  // otherwise { mode: 'single' | 'all', groups, instanceIds, captures, busy }.
  // `captures` holds the version each instance pinned when the dialog was built,
  // so Confirm can never substitute a version published in the meantime.
  const [confirmState, setConfirmState] = useState(null);
  const confirmingRef = useRef(false);
  // Live run documents per instance. The shared result hydrator below supplies
  // the corresponding preview/loading/ready/error lifecycle.
  const [runDocs, setRunDocs] = useState({});
  // The run-document subscription failed for an instance: its output stays, its
  // provenance line says so and offers a resubscribe.
  const [runDocErrors, setRunDocErrors] = useState({});
  const [runDocAttempt, setRunDocAttempt] = useState(0);
  // Unified is the default: two cards reading `region` almost always mean the
  // same region, and setting it once per card is the usual complaint about a
  // multi-card view.
  const [unifyParameters, setUnifyParameters] = useState(true);
  const [paramsCollapsed, setParamsCollapsed] = useState(false);
  // The parameters rail collapses to a slim 56px strip.
  const paramsWidth = paramsCollapsed ? '56px' : '280px';
  // Reported up by the coverage panel, purely so the freshness line can say
  // whether anything is refreshing this view on its own.
  const [hasEnabledAutomation, setHasEnabledAutomation] = useState(false);
  // The COMPLETED runs at the head of the company-wide recency stream, newest
  // first.
  const [streamHeadRuns, setStreamHeadRuns] = useState([]);
  const liveRunsRef = useRef(null);
  // Per card, the parameters of the run it shows: set when the card is
  // hydrated and again each time a run is adopted or started. When a newer run
  // of the card lands, the rail follows it only if it still equals these — a
  // half-edited filter the person has not run is never overwritten.
  const runParametersRef = useRef({});
  // Whether the functions this view shows have been resolved for the current
  // document. The coverage panel reconciles the view's automations against
  // them, so it must never see the empty map a load starts from.
  const [contractsReady, setContractsReady] = useState(false);
  const runSubscriptionsRef = useRef(new Map());
  const instanceResultsRef = useRef(instanceResults);
  instanceResultsRef.current = instanceResults;

  const acceptHydratedResult = useCallback(({
    targetId,
    runId,
    generation,
    data,
  }) => {
    const current = instanceResultsRef.current[targetId];
    if (!current || current.runId !== runId) return false;
    setInstanceResults(prev => {
      const latest = prev[targetId];
      if (!latest || latest.runId !== runId) return prev;
      return {
        ...prev,
        [targetId]: {
          ...latest,
          outputs: data?.outputs && typeof data.outputs === 'object' ? data.outputs : {},
          truncatedTables: Array.isArray(data?.truncated_tables)
            ? data.truncated_tables
            : latest.truncatedTables,
          resultGeneration: generation,
          fullLoaded: true,
          historySaveUnconfirmed: false,
        },
      };
    });
    return true;
  }, []);

  const {
    states: resultHydrations,
    prepare: prepareResultHydration,
    hydrate: hydrateResult,
    retry: retryResultHydration,
    clear: clearResultHydration,
  } = useRunResultHydration({
    scopeKey: `${companyName || ''}::view::${viewData?.id || ''}`,
    onHydrated: acceptHydratedResult,
  });

  // The widget model of this view, held locally: moving, resizing and display
  // changes made here never write the saved View.
  const [widgets, setWidgets] = useState({});
  const [layouts, setLayouts] = useState([]);
  const [editorWidgetId, setEditorWidgetId] = useState(null);

  useEffect(() => {
    const model = resolveViewModel(viewData);
    setWidgets(model.widgets);
    setLayouts(model.layouts);
    setEditorWidgetId(null);
  }, [viewData]);

  const handleToggleExclude = (instanceId, blockId, col) => {
    const key = `${instanceId}::${blockId}::${col}`;
    setExcludedParams(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Grid auto-width handled by WidthProvider
  const AutoWidthGrid = useMemo(() => WidthProvider(GridLayout), []);

  // Viewing changes are local. Only the leased View Builder saves shared
  // layout and display definitions.
  const handleLayoutCommit = useCallback((nextLayout) => {
    setLayouts(nextLayout.map((item) => (
      widgets[item.i] ? withDisplayConstraints(item, widgets[item.i].display) : item
    )));
  }, [widgets]);

  const handleWidgetSave = useCallback((widgetId, nextWidget) => {
    const nextWidgets = { ...widgets, [widgetId]: nextWidget };
    setWidgets(nextWidgets);
    // A display change can raise the minimum footprint (a stat tile becoming a
    // table, say), so the layout item is re-constrained alongside it.
    const nextLayouts = layouts.map((item) => (
      item.i === widgetId ? withDisplayConstraints(item, nextWidget.display) : item
    ));
    setLayouts(nextLayouts);
    setEditorWidgetId(null);
  }, [widgets, layouts]);

  const functionInstanceMap = useMemo(() => viewData?.functionInstanceMap || {}, [viewData]);

  const originalFunctionId = useCallback((instanceId) => (
    functionInstanceMap[instanceId] || instanceId
  ), [functionInstanceMap]);

  const instanceIds = useMemo(() => widgetInstanceIds(widgets), [widgets]);
  // The cards that can run: their function is published and not invalidated.
  // Only these have a panel of their own in the sidebar.
  const runnableInstanceIds = instanceIds.filter((instanceId) => {
    const func = functionsMap[originalFunctionId(instanceId)];
    return Boolean(func) && !func.invalid && Boolean(func.publishedVersion);
  });
  const preparedParameters = useMemo(() => Object.fromEntries(
    Object.entries(parameters).map(([instanceId, controls]) => [instanceId, prepareParameterControls(
      controls, functionsMap[originalFunctionId(instanceId)]?.data_source_blocks,
      excludedParams, `${instanceId}::`,
    )]),
  ), [parameters, functionsMap, originalFunctionId, excludedParams]);
  // A stable signature so the hydration effect does not re-run on every render.
  const instanceSignature = useMemo(() => instanceIds.join('|'), [instanceIds]);

  // The Dashboard's function list is live. The initial load only needs it to
  // look a function up by id, so it is read through a ref rather than listed as
  // a dependency — otherwise every draft edit to any function (a node dragged in
  // someone's builder) would re-initialise the whole view.
  const functionsRef = useRef(functions);
  functionsRef.current = functions;

  // The live contract watch (below) rebuilds a function's cards when its
  // published version changes. It compares each head document it sees against
  // the contract those cards were built from — `{ version, invalid }` per
  // function; the initial load reconciles the two once it has finished (on the
  // render after its state lands), so a publish during that load is not lost.
  // The epoch retires every rebuild started for a previous view.
  const loadedContractsRef = useRef({});
  const latestHeadsRef = useRef({});
  // funcId -> the head has edits its published version does not. A draft edit
  // rebuilds nothing (the cards run the published version), but every card of
  // that function says its published look is not the latest saved one.
  const [unpublishedHeads, setUnpublishedHeads] = useState({});
  // funcId -> the head's live `chart_settings`: display settings for every
  // chart of that function, applied to whatever run a card shows.
  const [headChartSettings, setHeadChartSettings] = useState({});
  const rebuildTokensRef = useRef({});
  const contractEpochRef = useRef(0);
  const [contractReconcile, setContractReconcile] = useState(0);
  const applyHeadRef = useRef(null);
  const parametersRef = useRef(parameters);
  parametersRef.current = parameters;
  const excludedParamsRef = useRef(excludedParams);
  excludedParamsRef.current = excludedParams;
  const functionsMapRef = useRef(functionsMap);
  functionsMapRef.current = functionsMap;
  const tableSchemasRef = useRef(tableSchemas);
  tableSchemasRef.current = tableSchemas;

  // Follow a completed run on screen without changing the saved View.
  const applyRailFromRuns = useCallback((railFromRun) => {
    const cardIds = Object.keys(railFromRun || {});
    if (cardIds.length === 0) return;
    setParameters((prev) => {
      const next = { ...prev };
      cardIds.forEach((instanceId) => {
        const { functionParams } = railFromRun[instanceId];
        if (Object.keys(functionParams).length > 0) next[instanceId] = functionParams;
      });
      return next;
    });
    setExcludedParams((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([key, value]) => {
        if (!cardIds.some((instanceId) => key.startsWith(`${instanceId}::`))) next[key] = value;
      });
      cardIds.forEach((instanceId) => {
        Object.keys(railFromRun[instanceId].excludedColumns).forEach((column) => {
          next[`${instanceId}::${column}`] = true;
        });
      });
      return next;
    });
  }, []);

  // Initialise every instance: its published parameter contract with that
  // version's default values, and the latest completed run that hydrates its
  // widgets.
  useEffect(() => {
    if (!viewData || !companyName) return;
    const ids = instanceSignature ? instanceSignature.split('|') : [];
    loadedContractsRef.current = {};
    contractEpochRef.current += 1;
    setContractsReady(false);
    runParametersRef.current = {};
    setVersionNotices({});
    if (ids.length === 0) {
      setContractsReady(true);
      setParameters({});
      setParameterAttributeColumns([]);
      setFunctionsMap({});
      setInstanceResults({});
      setRunDocs({});
      setErrors({});
      setHydrating({});
      clearResultHydration();
      return;
    }

    let cancelled = false;
    const historyController = new AbortController();

    const initializeData = async () => {
      const initialParams = {};
      const initialExcluded = {};
      const initialErrors = {};
      const functionDetailsMap = {};
      const tableIdsToFetch = new Set();

      setHydrating(ids.reduce((acc, instanceId) => ({ ...acc, [instanceId]: true }), {}));

      // Parameter forms derive from the PUBLISHED version's filters, not from the
      // draft: a draft that renames or removes a filter changes nothing here
      // until it is published.
      for (const instanceId of ids) {
        const funcId = originalFunctionId(instanceId);
        if (functionDetailsMap[funcId]) continue;
        const func = (functionsRef.current || []).find(f => f.id === funcId);
        if (!func) continue;
        const target = !func.invalid ? await loadRunTarget(companyName, funcId) : null;
        functionDetailsMap[funcId] = {
          ...func,
          ...(target?.head || {}),
          publishedVersion: target?.publishedVersion || null,
          versionContent: target?.content || null,
          data_source_blocks: versionDataSourceBlocks(target?.content),
        };
      }
      if (cancelled) return;

      ids.forEach((instanceId) => {
        const func = functionDetailsMap[originalFunctionId(instanceId)];
        initialErrors[instanceId] = instanceBlockReason(func);
        if (!func || func.invalid) return;
        if (!func.data_source_blocks) return;
        const built = buildInstanceControls(func.data_source_blocks);
        if (Object.keys(built.functionParams).length > 0) {
          initialParams[instanceId] = built.functionParams;
        }
        Object.keys(built.excludedColumns).forEach((column) => {
          initialExcluded[`${instanceId}::${column}`] = true;
        });
        built.tableIds.forEach((tableId) => tableIdsToFetch.add(tableId));
      });

      const fetchedSchemas = await fetchTableSchemas(companyName, Array.from(tableIdsToFetch));
      if (cancelled) return;
      setTableSchemas(fetchedSchemas);

      const attributeColumns = new Set();
      Object.values(functionDetailsMap).forEach((func) => {
        attributeColumnsFor(func.data_source_blocks, fetchedSchemas).forEach((column) => attributeColumns.add(column));
      });
      setParameterAttributeColumns(Array.from(attributeColumns).sort());

      setParameters(initialParams);
      setExcludedParams(initialExcluded);
      parametersRef.current = initialParams;
      excludedParamsRef.current = initialExcluded;
      setErrors(initialErrors);
      setIsLoading({});
      setFunctionsMap(functionDetailsMap);
      setContractsReady(true);

      // From here the live contract watch owns version changes. A head that
      // arrived while this load was running is reconciled on the next render
      // (once the state set above is what the refs see) rather than lost.
      const loadedContracts = {};
      Object.entries(functionDetailsMap).forEach(([funcId, func]) => {
        loadedContracts[funcId] = { version: func.publishedVersion || null, invalid: Boolean(func.invalid) };
      });
      loadedContractsRef.current = loadedContracts;
      setContractReconcile((tick) => tick + 1);

      // Hydrate every card from the latest COMPLETED run EXECUTED FOR THAT
      // CARD: its own Run button or the automation configured for it — both
      // stamp the run with the card (utils/viewCards.js). A run of the same
      // function from anywhere else, whatever its filters, never does. The
      // run's bounded `result_preview` paints straight away; the
      // generation-guarded effect below then downloads the complete result from
      // storage and replaces it, so a truncated table or a stat over 50,000 rows
      // becomes correct without the dashboard ever being blank.
      //
      // One shared read of the recent-runs window answers every instance at
      // once — per-function filtering would need a composite index — and the scan
      // continues until every card is found or history ends. Pending runs join
      // the live observer as they are read.
      const wantedParameters = {};
      const hydrationTargets = [];
      ids.forEach((instanceId) => {
        const funcId = originalFunctionId(instanceId);
        const func = functionDetailsMap[funcId];
        if (!func || func.invalid) return;
        const wanted = prepareParameterControls(
          initialParams[instanceId] || {},
          func.data_source_blocks,
          initialExcluded,
          instanceId + '::',
        ).parameters;
        wantedParameters[instanceId] = wanted;
        hydrationTargets.push({
          key: instanceId,
          functionId: funcId,
          matches: (run) => runBelongsToCard(run, viewData.id, instanceId),
        });
      });
      runParametersRef.current = wantedParameters;

      let runsByInstance = {};
      let hydrationFailed = false;
      try {
        runsByInstance = await loadLatestCompletedRunsFor(companyName, hydrationTargets, {
          signal: historyController.signal,
          onRuns: runs => liveRunsRef.current?.observeRuns(runs),
        });
      } catch (err) {
        console.error('Failed to hydrate this view from recent runs:', err);
        hydrationFailed = true;
      }
      if (cancelled) return;
      if (hydrationFailed) {
        // Each card says its latest result could not be read, rather than looking
        // like a function that has never produced one.
        setErrors((prev) => {
          const next = { ...prev };
          hydrationTargets.forEach(({ key: instanceId }) => {
            if (!next[instanceId]) next[instanceId] = loadFailureMessage('the latest result');
          });
          return next;
        });
      }

      const hydrated = {};
      const hydratedRuns = {};
      ids.forEach((instanceId) => {
        const run = runsByInstance[instanceId];
        if (!run) return;
        hydrated[instanceId] = hydrateFromRun(run);
        hydratedRuns[instanceId] = run;
      });

      // A card whose run was executed with other parameters than the defaults
      // — an automation with dynamic dates, or a person's own filters — shows
      // that run's parameters on its rail, so the controls describe the
      // figures on screen.
      const histories = await Promise.all(Object.entries(hydratedRuns).map(async ([instanceId, run]) => {
        const func = functionDetailsMap[originalFunctionId(instanceId)];
        const controls = parametersRef.current[instanceId];
        try {
          const prepared = await prepareHistoricalRunParameters(companyName, run, {
            functionId: func.id, version: func.publishedVersion, content: func.versionContent,
          });
          return { instanceId, func, controls, prepared };
        } catch (error) {
          return { instanceId, func, controls, prepared: { errors: [{ message: error.message }] } };
        }
      }));
      if (cancelled) return;
      const railFromRun = {};
      const runNotices = {};
      histories.forEach(({ instanceId, func, controls, prepared }) => {
        if (loadedContractsRef.current[func.id]?.version !== func.publishedVersion) return;
        // The run's parameters cannot be shown on the sidebar: the card keeps
        // its result and its default filters, says so, and stays runnable.
        if (prepared.errors.length > 0) {
          runNotices[instanceId] = historicalRunNotice(hydratedRuns[instanceId], prepared.errors, func.data_source_blocks);
          return;
        }
        const railNow = prepareParameterControls(
          parametersRef.current[instanceId], func.data_source_blocks,
          excludedParamsRef.current, `${instanceId}::`,
        ).parameters;
        const untouched = controls === initialParams[instanceId] && controls === parametersRef.current[instanceId]
          && railNow !== null && sameRunParameters(railNow, wantedParameters[instanceId]);
        if (untouched && !sameRunParameters(prepared.parameters, wantedParameters[instanceId])) {
          railFromRun[instanceId] = controlsFromRunParameters(func.data_source_blocks, prepared.parameters);
        }
        runParametersRef.current[instanceId] = prepared.parameters;
      });
      setVersionNotices((previous) => ({ ...previous, ...runNotices }));
      applyRailFromRuns(railFromRun);

      clearResultHydration();
      Object.entries(hydratedRuns).forEach(([instanceId, run]) => {
        prepareResultHydration(instanceId, run);
      });
      setInstanceResults(hydrated);
      setRunDocs(hydratedRuns);
      setHydrating({});
    };

    setLoadError(false);
    initializeData().catch((err) => {
      console.error('Failed to initialise the view:', err);
      if (cancelled) return;
      setHydrating({});
      setLoadError(true);
    });

    return () => { cancelled = true; historyController.abort(); };
  }, [
    loadAttempt,
    viewData,
    companyName,
    instanceSignature,
    originalFunctionId,
    clearResultHydration,
    prepareResultHydration,
    applyRailFromRuns,
  ]);

  // --- Live contract watch (H55) ------------------------------------------
  // One head listener per function this view uses. Draft edits — node moves,
  // renames, autosaves — change the head without changing `published_version`
  // and are ignored; a publish (or an invalid flag flip, or a deletion) rebuilds
  // that function's cards in place, keeping every on-screen value the new
  // version still accepts, and says so on the card. Runs from this view always
  // execute the current published version, so the controls must track it.
  const applyHead = useCallback(async (funcId, head) => {
    const loaded = loadedContractsRef.current[funcId];
    // The initial load has not built these cards yet; it reconciles when done.
    if (!loaded) return;
    // A refused preflight must not erase locally edited parameters. Reflect
    // the temporary freeze in Run controls without rebuilding their values.
    setFunctionsMap((previous) => {
      const current = previous[funcId];
      if (!current || current.status === head?.status) return previous;
      return { ...previous, [funcId]: { ...current, status: head?.status } };
    });
    if (isResourceDeleting(head) && !head.deletion_started_at) {
      rebuildTokensRef.current[funcId] = (rebuildTokensRef.current[funcId] || 0) + 1;
      return;
    }
    const prevVersion = loaded.version;
    const nextVersion = isPublishedVersion(head?.published_version) ? head.published_version : null;
    const nextInvalid = head ? Boolean(head.invalid) || isResourceDeleting(head) : true;
    if (nextVersion === prevVersion && nextInvalid === loaded.invalid) return;

    // Only the newest rebuild for a function may land; an older one still
    // reading its version is dropped, as is any rebuild once the view changes.
    const epoch = contractEpochRef.current;
    const token = (rebuildTokensRef.current[funcId] || 0) + 1;
    rebuildTokensRef.current[funcId] = token;
    const stale = () => rebuildTokensRef.current[funcId] !== token
      || contractEpochRef.current !== epoch
      || !loadedContractsRef.current[funcId];

    const content = nextVersion && !nextInvalid
      ? await loadFunctionVersion(companyName, funcId, nextVersion)
      : null;
    if (stale()) return;
    const dataSourceBlocks = versionDataSourceBlocks(content);
    const base = functionsMapRef.current[funcId]
      || (functionsRef.current || []).find((f) => f.id === funcId)
      || { id: funcId };
    const func = head
      ? {
        ...base,
        ...head,
        invalid: nextInvalid,
        invalid_reason: isResourceDeleting(head) ? 'This Function is being deleted.' : head.invalid_reason,
        id: funcId,
        publishedVersion: nextVersion,
        versionContent: content,
        data_source_blocks: dataSourceBlocks,
      }
      : { ...base, id: funcId, invalid: true, invalid_reason: "This card's function was deleted", publishedVersion: null, versionContent: null, data_source_blocks: {} };

    const affected = instanceIds.filter((instanceId) => originalFunctionId(instanceId) === funcId);
    const contractDifferences = prevVersion && nextVersion
      ? parameterCompatibilityErrors(base.data_source_blocks, dataSourceBlocks)
      : [];
    const dataSourcesChanged = contractDifferences.length > 0;
    const rebuilt = {};
    const tableIds = new Set();
    affected.forEach((instanceId) => {
      // An unchanged data-source contract carries every on-screen value over.
      // A changed one resets the card to the new version's defaults, as does a
      // card that had no controls (its function was invalid or unpublished).
      const onScreenControls = parametersRef.current[instanceId]
        ? foldExcluded(instanceId, parametersRef.current[instanceId], excludedParamsRef.current)
        : null;
      const previousControls = dataSourcesChanged ? null : onScreenControls;
      rebuilt[instanceId] = buildInstanceControls(func.invalid ? {} : dataSourceBlocks, previousControls);
      rebuilt[instanceId].tableIds.forEach((tableId) => tableIds.add(tableId));
    });

    const missingTables = Array.from(tableIds).filter((tableId) => !tableSchemasRef.current[tableId]);
    const newSchemas = await fetchTableSchemas(companyName, missingTables);
    const schemas = { ...tableSchemasRef.current, ...newSchemas };
    const neededColumns = attributeColumnsFor(dataSourceBlocks, schemas);
    if (stale()) return;

    loadedContractsRef.current = {
      ...loadedContractsRef.current,
      [funcId]: { version: nextVersion, invalid: Boolean(func.invalid) },
    };
    setFunctionsMap((prev) => ({ ...prev, [funcId]: func }));
    if (missingTables.length > 0) setTableSchemas((prev) => ({ ...prev, ...newSchemas }));
    setParameterAttributeColumns((prev) => Array.from(new Set([...prev, ...neededColumns])).sort());
    setParameters((prev) => {
      const next = { ...prev };
      affected.forEach((instanceId) => {
        const { functionParams } = rebuilt[instanceId];
        if (Object.keys(functionParams).length > 0) next[instanceId] = functionParams;
        else delete next[instanceId];
      });
      return next;
    });
    setExcludedParams((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([key, value]) => {
        if (!affected.some((instanceId) => key.startsWith(`${instanceId}::`))) next[key] = value;
      });
      affected.forEach((instanceId) => {
        Object.keys(rebuilt[instanceId].excludedColumns).forEach((column) => {
          next[`${instanceId}::${column}`] = true;
        });
      });
      return next;
    });
    // The rebuilt card shows runs of the version it now runs against, executed
    // with the values it now holds — a card whose function has just been
    // published for the first time starts adopting them here.
    affected.forEach((instanceId) => {
      if (func.invalid) {
        delete runParametersRef.current[instanceId];
        return;
      }
      const excluded = {};
      Object.keys(rebuilt[instanceId].excludedColumns).forEach((column) => {
        excluded[`${instanceId}::${column}`] = true;
      });
      runParametersRef.current[instanceId] = prepareParameterControls(
        rebuilt[instanceId].functionParams,
        dataSourceBlocks,
        excluded,
        instanceId + '::',
      ).parameters;
    });
    // A function that can no longer run replaces the card's result with the
    // reason. Otherwise the card keeps its result and a banner says what was
    // published. The only errors a publish lifts are the ones that were about
    // the previous version: why that version could not run, and the refusal of
    // a run clicked before this rebuild landed. Any other error — a run that
    // failed, a result that could not be read — is still true and stays.
    const blockReason = instanceBlockReason(func);
    const previousBlockReason = instanceBlockReason(base);
    const notice = blockReason ? null : publishNotice(prevVersion, nextVersion, contractDifferences);
    setErrors((prev) => {
      const next = { ...prev };
      affected.forEach((instanceId) => {
        if (blockReason) {
          next[instanceId] = blockReason;
          return;
        }
        const wasAboutThePreviousVersion = next[instanceId] === previousBlockReason
          || next[instanceId] === CONTRACT_CHANGED_MESSAGE;
        if (wasAboutThePreviousVersion) next[instanceId] = '';
      });
      return next;
    });
    setVersionNotices((prev) => {
      const next = { ...prev };
      affected.forEach((instanceId) => {
        if (notice) next[instanceId] = notice;
        else delete next[instanceId];
      });
      return next;
    });
  }, [companyName, instanceIds, originalFunctionId]);
  applyHeadRef.current = applyHead;

  // The initial load's reconcile: every head seen while it ran, applied now
  // that the controls it built are the ones on screen.
  useEffect(() => {
    if (contractReconcile === 0) return;
    Object.keys(loadedContractsRef.current).forEach((funcId) => {
      const head = latestHeadsRef.current[funcId];
      if (head === undefined) return;
      applyHeadRef.current?.(funcId, head).catch((err) => console.error('Failed to refresh a function contract:', err));
    });
  }, [contractReconcile]);

  useEffect(() => {
    if (!companyName) return undefined;
    const ids = instanceSignature ? instanceSignature.split('|') : [];
    const funcIds = Array.from(new Set(ids.map((instanceId) => originalFunctionId(instanceId))));
    latestHeadsRef.current = {};
    setUnpublishedHeads({});
    setHeadChartSettings({});
    const unsubscribes = funcIds.map((funcId) => onSnapshot(
      doc(db, `users/${companyName}/data_functions`, funcId),
      (snapshot) => {
        const head = snapshot.exists() ? snapshot.data() : null;
        latestHeadsRef.current[funcId] = head;
        const dirty = head?.has_unpublished_changes === true;
        setUnpublishedHeads((prev) => (prev[funcId] === dirty ? prev : { ...prev, [funcId]: dirty }));
        setHeadChartSettings((prev) => ({ ...prev, [funcId]: head?.chart_settings ?? null }));
        applyHeadRef.current?.(funcId, head).catch((err) => console.error('Failed to refresh a function contract:', err));
      },
      (err) => console.error('Failed to watch a function of this view:', err),
    ));
    return () => { unsubscribes.forEach((unsubscribe) => unsubscribe()); };
  }, [companyName, instanceSignature, originalFunctionId]);

  // Parameter edits remain local until explicitly used by Run or Keep current.
  const handleParameterChange = (instanceId, blockId, column, newValue) => {
    setParameters(prev => {
      const funcParams = { ...(prev[instanceId] || {}) };
      if (!funcParams[blockId]) return prev;

      return {
        ...prev,
        [instanceId]: {
          ...funcParams,
          [blockId]: {
            ...funcParams[blockId],
            data: {
              ...funcParams[blockId].data,
              [column]: newValue
            }
          }
        }
      };
    });
  };

  // Every VARIABLE column of every instance, merged by column name. The rail
  // edits one control and writes the value into each instance/block pairing that
  // declares that column — the same fan-out the Function pane does across the
  // blocks of one function, one level up.
  //
  // Only variable filters appear here: a filter the published version fixed
  // cannot be changed by anyone, so offering it merged would be a lie.
  // One control per column across every card's blocks — only where those
  // blocks agree on what the column is (type, range-or-value, default:
  // `filterSignature`). A column they disagree on cannot be driven by one
  // control, so it is listed in `unmergeableColumns` and shown per card.
  const unifiedModel = useMemo(() => {
    const byColumn = {};

    Object.entries(parameters).forEach(([instanceId, funcParams]) => {
      const func = functionsMap[originalFunctionId(instanceId)];
      if (!func || func.invalid) return;

      Object.entries(funcParams).forEach(([blockId, blockObj]) => {
        const blockFilters = func.data_source_blocks?.[blockId]?.filters || [];
        Object.entries(blockObj.data || {}).forEach(([col, value]) => {
          const filter = blockFilters.find(f => f.column === col);
          if (!filter?.variable) return;

          if (!byColumn[col]) {
            byColumn[col] = {
              name: col,
              targets: [],
              values: [],
              isRange: false,
              isAttribute: false,
              isDateRange: isDateRangeParam(col),
              tableIds: new Set(),
              type: resolveParameterType(col, filter.type),
              signatures: new Set(),
            };
          }
          const entry = byColumn[col];
          entry.signatures.add(filterSignature(filter));
          entry.targets.push({ instanceId, blockId });
          entry.values.push(value);
          if (filter.range) entry.isRange = true;
          if (blockObj.tableId) entry.tableIds.add(blockObj.tableId);

          const schema = tableSchemas[blockObj.tableId];
          const attributeColumns = [
            ...(schema?.attributeColumns || []),
            ...(schema?.tableAttributeColumns || []),
          ];
          if (attributeColumns.some(column => column?.name === col)) entry.isAttribute = true;
        });
      });
    });

    const unmergeable = new Set(
      Object.values(byColumn).filter((entry) => entry.signatures.size > 1).map((entry) => entry.name),
    );
    // Cards that currently hold different values (or IS/NOT bits) for a
    // unified column are shown per card as well, until they agree: one
    // control would show blank while Run All sent every card its own value
    // (M218). hiddenDisagreements reads one form of blocks, so every card's
    // blocks are flattened under `${instanceId}::${blockId}` — which also makes
    // the keys it checks the rail's own `${instanceId}::${blockId}::${column}`.
    const flatForm = {};
    const flatBlocks = {};
    Object.entries(parameters).forEach(([instanceId, funcParams]) => {
      const func = functionsMap[originalFunctionId(instanceId)];
      if (!func || func.invalid) return;
      Object.entries(funcParams).forEach(([blockId, blockObj]) => {
        flatForm[`${instanceId}::${blockId}`] = blockObj;
        flatBlocks[`${instanceId}::${blockId}`] = func.data_source_blocks?.[blockId];
      });
    });
    const differing = new Set(hiddenDisagreements(flatForm, excludedParams, flatBlocks));
    const list = Object.values(byColumn)
      .filter((entry) => !unmergeable.has(entry.name) && !differing.has(entry.name))
      .map((entry) => {
        // Every card holds this value: a column the cards hold differently is
        // listed per card below, never merged.
        const [first] = entry.values;
        const { signatures, ...rest } = entry;
        return {
          ...rest,
          tableIds: Array.from(entry.tableIds),
          value: first,
        };
      })
      .sort(compareParameters);
    return { list, unmergeable, differing };
  }, [parameters, excludedParams, functionsMap, originalFunctionId, tableSchemas]);
  const unifiedParameters = unifiedModel.list;
  const unmergeableColumns = unifiedModel.unmergeable;
  const differingColumns = unifiedModel.differing;
  // A card has one panel and one date-draft scope even when both its filter
  // definitions and its values differ from other cards.
  const perCardColumns = new Set([...unmergeableColumns, ...differingColumns]);

  const handleUnifiedParameterChange = (param, newValue) => {
    setParameters(prev => {
      const next = { ...prev };
      param.targets.forEach(({ instanceId, blockId }) => {
        const funcParams = next[instanceId];
        if (!funcParams?.[blockId]) return;
        next[instanceId] = {
          ...funcParams,
          [blockId]: {
            ...funcParams[blockId],
            data: { ...funcParams[blockId].data, [param.name]: newValue },
          },
        };
      });
      return next;
    });
  };

  // Excluding is stored per instance, so the unified chip flips every instance
  // that carries the column — and reads as excluded only when they all are.
  const handleUnifiedToggleExclude = (param) => {
    const allExcluded = param.targets.every(
      ({ instanceId, blockId }) => excludedParams[`${instanceId}::${blockId}::${param.name}`]
    );
    setExcludedParams(prev => {
      const next = { ...prev };
      param.targets.forEach(({ instanceId, blockId }) => {
        next[`${instanceId}::${blockId}::${param.name}`] = !allExcluded;
      });
      return next;
    });
  };

  // Clear every parameter of every card and drop all exclusions. Only variable
  // filters ever reach `parameters` (see the init effect), so everything here is
  // editable. Clearing, like every parameter edit, belongs to this local view.
  const hasActiveParameters = Object.values(dateDrafts).some(Boolean)
    || Object.values(excludedParams || {}).some(Boolean)
    || Object.values(parameters || {}).some((funcParams) => (
      Object.values(funcParams || {}).some((blockObj) => (
        Object.values(blockObj?.data || {}).some((value) => !isEmptyParameterValue(value))
      ))
    ));

  const handleClearParameters = () => {
    const next = {};
    instanceIds.forEach((instanceId) => {
      const blocks = functionsMap[originalFunctionId(instanceId)]?.data_source_blocks || {};
      next[instanceId] = buildInstanceControls(blocks, {}).functionParams;
    });
    setParameters(next);
    setExcludedParams({});
    setVersionNotices({});
    setParameterClearGeneration((n) => n + 1);
  };

  const dismissVersionNotice = useCallback((instanceId) => {
    setVersionNotices((previous) => {
      const next = { ...previous };
      delete next[instanceId];
      return next;
    });
  }, []);

  const isUnifiedExcluded = (param) => (
    param.targets.length > 0
      && param.targets.every(({ instanceId, blockId }) => excludedParams[`${instanceId}::${blockId}::${param.name}`])
  );

  // A text column is searched on the server only where the table actually
  // indexes it — never for an external table, which Decode reads through rather
  // than stores and therefore cannot index.
  const indexedTableIdsFor = useCallback((name, type, tableIds) => (
    (tableIds || []).filter((tableId) => (
      tableId
      && type === 'Text'
      && !isExternalTable(tableSchemas?.[tableId])
      && tableSchemas?.[tableId]?.detailColumns?.some((column) => column?.name === name && column?.index)
    ))
  ), [tableSchemas]);

  const instanceFunction = useCallback((instanceId) => {
    const funcId = originalFunctionId(instanceId);
    return functionsMap[funcId] || functions.find(f => f.id === funcId);
  }, [functionsMap, functions, originalFunctionId]);

  // Read this instance's Function head and the version it points at, right now.
  // Every Run and Run All captures its own version this way, and the captured
  // number is what the request pins — a version published between Capture and
  // Confirm never substitutes itself into the run.
  // The card's message when the click-time read of a function's published version
  // fails — an ordinary network or Firestore error, distinct from "not published".
  const TARGET_READ_FAILED = loadFailureMessage('this function');

  const captureRunTarget = async (instanceId) => {
    const funcId = originalFunctionId(instanceId);
    const target = await loadRunTarget(companyName, funcId);
    if (!target) return null;
    if (isResourceDeleting(target.head) || isResourceDeleting(latestHeadsRef.current[funcId])
        || latestHeadsRef.current[funcId] === null) {
      return { ...target, originalFuncId: funcId, invalid: true, invalidReason: 'This Function is being deleted.' };
    }
    const dataSourceBlocks = versionDataSourceBlocks(target.content);
    // A publish the contract watch has not delivered yet: the controls on screen
    // belong to another version, so this run is refused rather than sent with
    // values the new version would drop. The watch rebuilds the cards moments
    // later and the user runs again from the refreshed controls.
    const loaded = loadedContractsRef.current[funcId];
    if (loaded && loaded.version !== target.publishedVersion) {
      return { ...target, originalFuncId: funcId, dataSourceBlocks, contractChanged: true };
    }
    setFunctionsMap(prev => (prev[funcId] ? {
      ...prev,
      [funcId]: {
        ...prev[funcId],
        ...target.head,
        publishedVersion: target.publishedVersion,
        versionContent: target.content,
        data_source_blocks: dataSourceBlocks,
      },
    } : prev));
    return { ...target, originalFuncId: funcId, dataSourceBlocks };
  };

  // Confirmation detail for one instance, resolved from the captured version's
  // Email Outputs plus the company contact book. The draft is never consulted.
  const buildInstanceConfirmation = async (instanceId, capture, contactsById = null) => {
    const emailOutputs = versionEmailOutputs(capture?.content);
    if (emailOutputs.length === 0) return null;
    const func = instanceFunction(instanceId);
    let byId = contactsById;
    if (!byId) {
      const contacts = await listContacts(companyName);
      byId = new Map();
      (contacts || []).forEach(contact => byId.set(contact.id || contact.contact_id, contact));
    }
    const outputs = emailOutputs.map((output) => ({
      blockId: output.blockId,
      nodeName: formatText(output.nodeName),
      subject: output.subject,
      recipients: output.recipientContactIds.map(id => recipientEntry(id, byId.get(id))),
      attachments: output.attachments.map((attachment) => attachmentLabel(attachment, formatText)),
    }));
    return {
      instanceId,
      functionName: func?.name || capture.originalFuncId,
      version: capture.publishedVersion,
      outputs,
    };
  };

  // Execute one instance against the version it captured. A 428 (effect
  // required) re-surfaces a single-instance confirmation for that same version.
  const executeInstance = async (instanceId, confirmed, capture) => {
    const funcId = originalFunctionId(instanceId);
    if (!capture || !isPublishedVersion(capture.publishedVersion) || capture.invalid) {
      setErrors(prev => ({
        ...prev,
        [instanceId]: capture?.invalid
          ? (capture.invalidReason || 'This function references a deleted resource')
          : 'Publish this function to run it',
      }));
      setIsLoading(prev => ({ ...prev, [instanceId]: false }));
      return;
    }

    setIsLoading(prev => ({ ...prev, [instanceId]: true }));
    setErrors(prev => ({ ...prev, [instanceId]: '' }));
    dismissVersionNotice(instanceId);
    // The previously displayed run document is deliberately KEPT until the new
    // one arrives: a card that blanks its provenance line the moment Run is
    // pressed reads as broken. The subscription below notices that the stored
    // document no longer matches the run in flight and re-subscribes.
    clearResultHydration(instanceId);

    try {
      // The nested `parameters` map, validated against the captured version's
      // filter contract. It is also what the card now shows runs FOR: the live
      // repaint adopts a newer run of this function only when it was executed
      // with these same parameters.
      const prepared = prepareParameterControls(
        parameters[instanceId] || {},
        capture.dataSourceBlocks,
        excludedParams,
        instanceId + '::',
      );
      if (prepared.errors.length > 0) {
        throw new Error(formatParameterErrors(prepared.errors, capture.dataSourceBlocks));
      }
      const requestParameters = prepared.parameters;
      runParametersRef.current[instanceId] = requestParameters;

      const envelope = {
        function_id: funcId,
        execution_mode: 'full',
        origin: 'view',
        view_context: { view_id: viewData.id || null, view_instance_id: instanceId },
        parameters: requestParameters,
        function_version: capture.publishedVersion,
        external_effects_confirmed: confirmed,
      };

      const result = await runFunctionEndpoint(envelope);
      const truncatedTables = Array.isArray(result?.truncated_tables) ? result.truncated_tables : [];
      setInstanceResults(prev => ({
        ...prev,
        [instanceId]: {
          runId: result?.runId || null,
          historySaveUnconfirmed: result?.persist_error != null,
          outputs: result?.outputs && typeof result.outputs === 'object' ? result.outputs : {},
          truncatedTables,
          resultGeneration: null,
          outputsSummary: null,
          // The manual response carries the complete composite, not the bounded
          // preview, so rows-consuming widgets are correct immediately.
          fullLoaded: true,
        },
      }));
    } catch (err) {
      if (err.status === 428 && !confirmed && isEmailFeatureEnabled()) {
        try {
          const confirmation = await buildInstanceConfirmation(instanceId, capture);
          if (confirmation) {
            setConfirmState({
              mode: 'single',
              groups: [confirmation],
              instanceIds: [instanceId],
              captures: { [instanceId]: capture },
              busy: false,
            });
            setIsLoading(prev => ({ ...prev, [instanceId]: false }));
            return;
          }
        } catch (confErr) {
          console.error('Failed to build email confirmation:', confErr);
        }
      }
      if (err.status === 409 && err.code === 'function_version_unavailable') {
        // Best-effort refresh of the card's contract; the message below stands either way.
        try {
          await captureRunTarget(instanceId);
        } catch (captureErr) {
          console.error(`Failed to re-read the function of ${instanceId} after a 409:`, captureErr);
        }
        setErrors(prev => ({
          ...prev,
          [instanceId]: 'This function version is no longer available. Run it again.',
        }));
      } else {
        console.error(`Error running function ${instanceId}:`, err);
        setErrors(prev => ({ ...prev, [instanceId]: `Failed to run: ${err.message}` }));
      }
    } finally {
      setIsLoading(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const setPreparingFor = (ids, value) => {
    if (ids.length === 0) return;
    ids.forEach((id) => (value ? preparingRef.current.add(id) : preparingRef.current.delete(id)));
    setPreparing((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (value) next[id] = true;
        else delete next[id];
      });
      return next;
    });
  };

  // Individual instance Run. An effectful instance shows ONE confirmation before
  // running and keeps its Run control disabled (via confirmState) until the
  // backend responds.
  const handleRunFunction = async (instanceId) => {
    if (!instanceId) return;
    // Wait for the stored run's parameters before starting a new run.
    if (hydrating[instanceId]) return;
    if (isLoading[instanceId] || preparingRef.current.has(instanceId)) return;
    setPreparingFor([instanceId], true);
    try {
      await runInstanceFromClick(instanceId);
    } finally {
      setPreparingFor([instanceId], false);
    }
  };

  const runInstanceFromClick = async (instanceId) => {
    if (revealDateDrafts(instanceId)) {
      setErrors(prev => ({ ...prev, [instanceId]: INCOMPLETE_DATES_MESSAGE }));
      setIsLoading(prev => ({ ...prev, [instanceId]: false }));
      return;
    }
    const invertedDates = invertedInstanceDates(instanceId);
    if (invertedDates) {
      setErrors(prev => ({ ...prev, [instanceId]: invertedDates }));
      setIsLoading(prev => ({ ...prev, [instanceId]: false }));
      return;
    }
    const parameterErrors = preparedParameters[instanceId]?.errors || [];
    if (parameterErrors.length > 0) {
      setErrors(prev => ({
        ...prev,
        [instanceId]: formatParameterErrors(parameterErrors, instanceFunction(instanceId)?.data_source_blocks),
      }));
      return;
    }
    // Running the card with the rail as shown makes those values the person's.
    const func = instanceFunction(instanceId);
    if (!func || func.invalid || isResourceDeleting(func)) {
      setErrors(prev => ({ ...prev, [instanceId]: instanceBlockReason(func) }));
      setIsLoading(prev => ({ ...prev, [instanceId]: false }));
      return;
    }

    let capture;
    try {
      capture = await captureRunTarget(instanceId);
    } catch (err) {
      console.error(`Failed to read the function of ${instanceId} before running:`, err);
      setErrors(prev => ({ ...prev, [instanceId]: TARGET_READ_FAILED }));
      return;
    }
    if (capture?.contractChanged) {
      setErrors(prev => ({ ...prev, [instanceId]: CONTRACT_CHANGED_MESSAGE }));
      return;
    }
    if (capture?.invalid) {
      setErrors(prev => ({ ...prev, [instanceId]: capture.invalidReason }));
      return;
    }
    if (!capture || !isPublishedVersion(capture.publishedVersion)) {
      setErrors(prev => ({ ...prev, [instanceId]: 'Publish this function to run it' }));
      return;
    }

    if (isEmailFeatureEnabled() && versionHasExternalEffect(capture.content)) {
      try {
        const confirmation = await buildInstanceConfirmation(instanceId, capture);
        if (confirmation) {
          setConfirmState({
            mode: 'single',
            groups: [confirmation],
            instanceIds: [instanceId],
            captures: { [instanceId]: capture },
            busy: false,
          });
          return;
        }
      } catch (err) {
        // Fall through to a normal run; the backend 428 re-surfaces confirmation.
        console.error('Failed to prepare email confirmation:', err);
      }
    }

    await executeInstance(instanceId, false, capture);
  };

  // True while any card is still loading its latest run. Until that finishes,
  // `parameters` may not yet describe the displayed run. Run All gates on
  // this; each instance's sidebar Run
  // button and handleRunFunction gate on their own `hydrating[instanceId]`. (The
  // widget body's `loading` flag also consults it, but that is display only.)
  const isHydrating = Object.values(hydrating).some(Boolean);

  // Run All. Non-effectful instances run immediately (the user elected to run
  // them). All effectful instances are grouped into ONE composite confirmation;
  // only confirmed instances are sent with external_effects_confirmed=true, and a
  // declined confirmation cancels only those effectful runs.
  const handleRunAll = async () => {
    if (instanceIds.length === 0) return;
    // Belt as well as braces: the button below is disabled while hydrating, but a
    // click landing in the same tick must not run this view's functions with the
    // previous view's filters, nor unfiltered.
    if (isHydrating) return;
    if (preparingRef.current.size > 0 || Object.values(isLoading).some(Boolean)) return;
    setPreparingFor(instanceIds, true);
    // Instances handed to the sequential runner below release themselves as each
    // run starts, so a queued card stays locked until its turn.
    const queued = new Set();
    try {
      await runAllFromClick(queued);
    } finally {
      setPreparingFor(instanceIds.filter((id) => !queued.has(id)), false);
    }
  };

  const runAllFromClick = async (queued) => {
    // Clear all errors up front.
    const newErrors = {};
    instanceIds.forEach(instanceId => {
      newErrors[instanceId] = instanceBlockReason(instanceFunction(instanceId));
    });
    // A card with a half-typed date is marked and skipped; the rest still run
    // (in unified mode the one control feeds every card, so every card waits).
    instanceIds.forEach((instanceId) => {
      if (!newErrors[instanceId] && revealDateDrafts(instanceId)) {
        newErrors[instanceId] = INCOMPLETE_DATES_MESSAGE;
      } else if (!newErrors[instanceId]) {
        newErrors[instanceId] = invertedInstanceDates(instanceId)
          || formatParameterErrors(preparedParameters[instanceId]?.errors, instanceFunction(instanceId)?.data_source_blocks);
      }
    });
    setErrors(newErrors);

    // Capture a version per instance INDEPENDENTLY, so each card runs the exact
    // version its own confirmation described.
    const captures = {};
    // A read that fails marks its own card and the rest still run.
    for (const instanceId of instanceIds) {
      const func = instanceFunction(instanceId);
      if (!func || func.invalid || isResourceDeleting(func) || newErrors[instanceId]) continue;
      let capture;
      try {
        capture = await captureRunTarget(instanceId);
      } catch (err) {
        console.error(`Failed to read the function of ${instanceId} before Run All:`, err);
        newErrors[instanceId] = TARGET_READ_FAILED;
        continue;
      }
      if (capture?.contractChanged) {
        newErrors[instanceId] = CONTRACT_CHANGED_MESSAGE;
        continue;
      }
      if (capture?.invalid) {
        newErrors[instanceId] = capture.invalidReason;
        continue;
      }
      if (!capture || !isPublishedVersion(capture.publishedVersion)) {
        newErrors[instanceId] = 'Publish this function to run it';
        continue;
      }
      captures[instanceId] = capture;
    }
    setErrors({ ...newErrors });

    const runnableInstanceIds = Object.keys(captures);
    const effectfulInstanceIds = isEmailFeatureEnabled()
      ? runnableInstanceIds.filter((instanceId) => versionHasExternalEffect(captures[instanceId].content))
      : [];
    const nonEffectfulInstanceIds = runnableInstanceIds.filter(id => !effectfulInstanceIds.includes(id));

    // Run non-effectful instances now, sequentially, without blocking the
    // composite confirmation from appearing. Each leaves the preparing gate as
    // its run starts; executeInstance raises isLoading in the same render.
    nonEffectfulInstanceIds.forEach((id) => queued.add(id));
    (async () => {
      for (const instanceId of nonEffectfulInstanceIds) {
        setPreparingFor([instanceId], false);
        await executeInstance(instanceId, false, captures[instanceId]);
      }
    })();

    if (effectfulInstanceIds.length === 0) return;

    try {
      const contacts = await listContacts(companyName);
      const contactsById = new Map();
      (contacts || []).forEach(contact => contactsById.set(contact.id || contact.contact_id, contact));
      const groups = [];
      for (const instanceId of effectfulInstanceIds) {
        const confirmation = await buildInstanceConfirmation(instanceId, captures[instanceId], contactsById);
        if (confirmation) groups.push(confirmation);
      }
      if (groups.length > 0) {
        setConfirmState({
          mode: 'all',
          groups,
          instanceIds: effectfulInstanceIds,
          captures,
          busy: false,
        });
      } else {
        for (const instanceId of effectfulInstanceIds) {
          await executeInstance(instanceId, false, captures[instanceId]);
        }
      }
    } catch (err) {
      console.error('Failed to prepare Run All confirmation:', err);
      for (const instanceId of effectfulInstanceIds) {
        await executeInstance(instanceId, false, captures[instanceId]);
      }
    }
  };

  // Confirm the pending effect confirmation: send external_effects_confirmed=true
  // for every confirmed instance, each against the version it captured when the
  // dialog was built.
  const handleConfirmRun = async () => {
    if (!confirmState || confirmingRef.current) return;
    const request = confirmState;
    const remainingIds = [...request.instanceIds];
    let keepOpen = false;
    confirmingRef.current = true;
    setConfirmState(previous => ({ ...previous, busy: true, error: '' }));
    try {
      for (const instanceId of request.instanceIds) {
        // Run All can take minutes: recheck each card just before it starts.
        // Sending still names the contact, not an address pinned by this UI.
        let fresh;
        let readError = '';
        try {
          fresh = await buildInstanceConfirmation(instanceId, request.captures[instanceId]);
          if (!fresh) throw new Error('The captured version has no Email Output.');
        } catch (error) {
          console.error('Failed to re-check View email recipients:', error);
          readError = 'The recipients could not be re-checked. Try again.';
        }
        const reviewed = request.groups.find(group => group.instanceId === instanceId);
        const changed = fresh && confirmationChanged(reviewed, fresh);
        if (readError || changed || (fresh && blockedRecipients(fresh).length > 0)) {
          keepOpen = true;
          // Completed cards leave the pending list, so reviewing a later
          // recipient change cannot send any earlier card a second time.
          const groups = request.groups.filter(group => remainingIds.includes(group.instanceId))
            .map(group => group.instanceId === instanceId && fresh ? fresh : group);
          setConfirmState({ ...request, instanceIds: remainingIds, groups,
            busy: false, changed, error: readError });
          return;
        }
        await executeInstance(instanceId, true, request.captures[instanceId]);
        remainingIds.shift();
      }
    } finally {
      confirmingRef.current = false;
      if (!keepOpen) setConfirmState(null);
    }
  };

  // A declined confirmation cancels only those runs; non-effectful instances the
  // user elected to run are unaffected (they have already started).
  const handleCancelRun = () => {
    if (confirmState?.busy) return;
    setConfirmState(null);
  };

  // --- Per-instance run subscriptions --------------------------------------
  // Each running card watches its own run document. A card stops watching only
  // when its run is terminal, every Email Output's projected result has caught
  // up with its authoritative state, and no result fetch is in flight — or
  // immediately when the run is replaced, the card is removed, or the view
  // unmounts. A hydrated run that is already settled is never subscribed at all.

  // The instances that still need a live subscription, as a stable signature.
  const activeSubscriptions = useMemo(() => {
    const active = [];
    Object.entries(instanceResults).forEach(([instanceId, result]) => {
      if (!result?.runId) return;
      const run = runDocs[instanceId];
      const hydration = resultHydrations[instanceId];
      const fetching = (
        hydration?.runId === result.runId
        && hydration.status === 'loading'
      );
      // A run document that has not been read yet — including the case where the
      // slot still holds the run this one just replaced — always needs watching,
      // however settled that older document looks.
      if (run?.id === result.runId && isRunSettled(run, fetching)) return;
      active.push(`${instanceId}::${result.runId}`);
    });
    return active.sort().join('|');
  }, [instanceResults, runDocs, resultHydrations]);

  useEffect(() => {
    const desired = new Map(
      activeSubscriptions
        .split('|')
        .filter(Boolean)
        .map((entry) => {
          const separator = entry.indexOf('::');
          return [entry.slice(0, separator), entry.slice(separator + 2)];
        }),
    );
    const subscriptions = runSubscriptionsRef.current;

    // Drop subscriptions for settled, replaced, or removed runs.
    subscriptions.forEach((entry, instanceId) => {
      if (desired.get(instanceId) !== entry.runId) {
        entry.unsubscribe();
        subscriptions.delete(instanceId);
      }
    });

    desired.forEach((runId, instanceId) => {
      if (subscriptions.has(instanceId) || !companyName) return;
      // A fresh subscription (a retry, or a new run) starts clean: a failure
      // recorded against the previous attempt must not label this one.
      setRunDocErrors(prev => (prev[instanceId] ? { ...prev, [instanceId]: false } : prev));
      const unsubscribe = onSnapshot(
        doc(db, `users/${companyName}/function_runs/${runId}`),
        (snap) => {
          if (!snap.exists()) return;
          const run = { id: snap.id, ...snap.data() };
          setRunDocs(prev => ({ ...prev, [instanceId]: run }));
          setRunDocErrors(prev => (prev[instanceId] ? { ...prev, [instanceId]: false } : prev));
          setInstanceResults(prev => {
            const current = prev[instanceId];
            if (!current || current.runId !== run.id) return prev;
            return { ...prev, [instanceId]: { ...current, outputsSummary: run.outputs_summary || null } };
          });
        },
        (err) => {
          console.error(`Failed to subscribe to run ${runId}:`, err);
          // A failed listener has stopped. Forget it so a retry (runDocAttempt)
          // subscribes afresh rather than finding this dead entry in place.
          subscriptions.delete(instanceId);
          setRunDocErrors(prev => ({ ...prev, [instanceId]: true }));
        },
      );
      subscriptions.set(instanceId, { runId, unsubscribe });
    });
  }, [activeSubscriptions, companyName, runDocAttempt]);

  // Refresh an instance's result only when its run acknowledges a newer
  // generation. This is also what upgrades a hydrated bounded preview to the
  // complete stored result.
  useEffect(() => {
    Object.entries(runDocs).forEach(([instanceId, run]) => {
      const result = instanceResults[instanceId];
      if (!result || !run || result.runId !== run.id || !run.result_gcs_path) return;
      hydrateResult(instanceId, run);
    });
  }, [runDocs, instanceResults, hydrateResult]);

  useEffect(() => {
    const subscriptions = runSubscriptionsRef.current;
    return () => {
      subscriptions.forEach((entry) => entry.unsubscribe());
      subscriptions.clear();
    };
  }, []);

  // One discovery stream plus document listeners for relevant pending runs.
  // History hydration also feeds this observer, so it watches pending runs
  // older than the live window. Retry reloads history to cover an outage gap.
  useEffect(() => {
    if (!companyName || !instanceSignature) return undefined;
    setStreamHeadRuns([]);
    setStreamError(false);
    const targets = instanceSignature.split('|').map(instanceId => ({
      key: instanceId,
      functionId: originalFunctionId(instanceId),
      matches: run => runBelongsToCard(run, viewData?.id, instanceId),
    }));
    const observer = watchCardRuns(companyName, targets, setStreamHeadRuns, error => {
      console.error('Failed to watch recent runs for this view:', error);
      setStreamError(true);
    });
    liveRunsRef.current = observer;
    return () => {
      observer.unsubscribe();
      if (liveRunsRef.current === observer) liveRunsRef.current = null;
    };
  }, [companyName, instanceSignature, originalFunctionId, viewData?.id, streamAttempt]);

  // Adopt a run from that stream only when it was executed FOR the card — its
  // Run button or its automation — and is STRICTLY newer than what the card
  // is showing. Two things are deliberately left alone: a run this pane
  // started itself, whose response already carried the COMPLETE composite that
  // the bounded preview would otherwise overwrite; and the per-run
  // subscriptions, which pick the new run up from `instanceResults` exactly as
  // they do after a manual run.
  useEffect(() => {
    if (isHydrating) return undefined;
    const arrivals = [];
    instanceIds.forEach((instanceId) => {
      const funcId = originalFunctionId(instanceId);
      const wanted = runParametersRef.current[instanceId];
      if (!wanted) return;
      const incoming = streamHeadRuns.find((run) => (
        run.function_id === funcId && runBelongsToCard(run, viewData?.id, instanceId)
      ));
      if (!incoming) return;
      if (instanceResults[instanceId]?.runId === incoming.id) return;

      const incomingAt = runCreatedAt(incoming)?.getTime();
      if (!Number.isFinite(incomingAt)) return;
      const displayedAt = runCreatedAt(runDocs[instanceId])?.getTime();
      if (Number.isFinite(displayedAt) && incomingAt <= displayedAt) return;

      arrivals.push([instanceId, incoming]);
    });
    if (arrivals.length === 0) return;

    // Check the historical contract before moving any run values into the
    // current controls. A failed metadata read leaves the output usable.
    let cancelled = false;
    const adoptArrivals = async () => {
      const histories = await Promise.all(arrivals.map(async ([instanceId, run]) => {
        const func = functionsMapRef.current[originalFunctionId(instanceId)];
        const controls = parametersRef.current[instanceId];
        const exclusions = excludedParamsRef.current;
        try {
          const prepared = await prepareHistoricalRunParameters(companyName, run, {
            functionId: func.id, version: func.publishedVersion, content: func.versionContent,
          });
          return { instanceId, run, func, controls, exclusions, prepared };
        } catch (error) {
          return { instanceId, run, func, controls, exclusions, prepared: { errors: [{ message: error.message }] } };
        }
      }));
      if (cancelled) return;
      const railFromRun = {};
      const runNotices = {};
      histories.forEach(({ instanceId, run, func, controls, exclusions, prepared }) => {
        const current = functionsMapRef.current[originalFunctionId(instanceId)];
        if (!current || current.invalid || current.publishedVersion !== func?.publishedVersion) return;
        const railNow = prepareParameterControls(
          parametersRef.current[instanceId] || {}, current.data_source_blocks,
          excludedParamsRef.current, `${instanceId}::`,
        ).parameters;
        const untouched = controls === parametersRef.current[instanceId] && exclusions === excludedParamsRef.current
          && railNow !== null && sameRunParameters(railNow, runParametersRef.current[instanceId]);
        if (prepared.errors.length > 0) {
          runNotices[instanceId] = historicalRunNotice(run, prepared.errors, current.data_source_blocks);
        } else if (untouched && !sameRunParameters(prepared.parameters, railNow)) {
          railFromRun[instanceId] = controlsFromRunParameters(current.data_source_blocks, prepared.parameters);
        }
        runParametersRef.current[instanceId] = prepared.parameters || run.parameters || {};
      });
      // An adopted run replaces whatever the card's notice said about the run
      // it showed before.
      setVersionNotices((previous) => {
        const next = { ...previous };
        arrivals.forEach(([instanceId]) => { delete next[instanceId]; });
        return { ...next, ...runNotices };
      });
      applyRailFromRuns(railFromRun);
      setErrors(previous => {
        const next = { ...previous };
        arrivals.forEach(([instanceId]) => { delete next[instanceId]; });
        return next;
      });
      arrivals.forEach(([instanceId, run]) => prepareResultHydration(instanceId, run));
      setRunDocs(previous => {
        const next = { ...previous };
        arrivals.forEach(([instanceId, run]) => { next[instanceId] = run; });
        return next;
      });
      setInstanceResults(previous => {
        const next = { ...previous };
        arrivals.forEach(([instanceId, run]) => { next[instanceId] = hydrateFromRun(run); });
        return next;
      });
    };
    adoptArrivals().catch((error) => {
      if (cancelled) return;
      console.error('Failed to adopt the latest View results:', error);
      setStreamError(true);
    });
    return () => { cancelled = true; };
  }, [
    companyName,
    streamHeadRuns,
    isHydrating,
    instanceIds,
    instanceResults,
    runDocs,
    originalFunctionId,
    prepareResultHydration,
    applyRailFromRuns,
    viewData,
  ]);

  // The newest result on screen, whatever produced it.
  const lastRefreshedAt = useMemo(() => {
    let newest = null;
    instanceIds.forEach((instanceId) => {
      const completed = runCompletedAt(runDocs[instanceId]);
      if (completed && (!newest || completed.getTime() > newest.getTime())) newest = completed;
    });
    return newest;
  }, [instanceIds, runDocs]);

  // The DISTINCT functions this view shows. Memoised because the coverage panel
  // takes it as an effect dependency. `publishedVersion` is the NORMALIZED head
  // value (a positive integer, or null) the automations this panel creates pin
  // themselves to — never the raw field, which may be a string or a float.
  // The cards the coverage panel can automate, in card order: a function's
  // published version and blocks, and the rail's current request parameters
  // (what a new automation is seeded with). An unpublished or invalid function
  // has nothing to run.
  const viewCards = useMemo(() => {
    const visible = instanceIds.flatMap((instanceId) => {
      const funcId = originalFunctionId(instanceId);
      const func = functionsMap[funcId];
      if (!func || func.invalid || !func.publishedVersion) return [];
      return [{
        instanceId,
        func: {
          id: funcId,
          name: func.name || funcId,
          publishedVersion: func.publishedVersion,
          data_source_blocks: func.data_source_blocks || {},
        },
      }];
    });
    // Labelled among the cards actually listed, so a hidden (invalid or
    // unpublished) card never earns a visible one an ordinal.
    const labels = cardLabels(visible.map(({ instanceId, func }) => ({ instanceId, functionName: func.name })));
    return visible.map(({ instanceId, func }) => {
      const prepared = preparedParameters[instanceId] || { parameters: {}, errors: [] };
      const incompleteDate = dateDrafts[instanceId] || (unifyParameters && dateDrafts[UNIFIED_DATE_SCOPE]);
      return {
        instanceId,
        label: labels[instanceId],
        func,
        railParameters: prepared.parameters,
        parameterError: incompleteDate
          ? INCOMPLETE_DATES_MESSAGE
          : formatParameterErrors(prepared.errors, func.data_source_blocks),
      };
    });
  }, [instanceIds, originalFunctionId, functionsMap, preparedParameters, dateDrafts, unifyParameters]);

  const viewTables = useMemo(
    () => Object.entries(tableSchemas).map(([id, table]) => ({ id, ...table })),
    [tableSchemas],
  );

  const handleAutomationChange = useCallback((enabled) => {
    setHasEnabledAutomation(enabled);
  }, []);

  const handleRetryStoredResult = useCallback((instanceId) => {
    const run = runDocs[instanceId];
    const result = instanceResultsRef.current[instanceId];
    if (!run || !result || result.runId !== run.id) return;
    retryResultHydration(instanceId, run);
  }, [runDocs, retryResultHydration]);

  // Block names of the versions that runs on screen pinned but the functions no
  // longer publish, keyed `${funcId}:${version}` (null = that version is gone).
  // Loaded on demand and never dropped: a version is immutable.
  const [historicalBlockNames, setHistoricalBlockNames] = useState({});
  useEffect(() => {
    if (!companyName) return;
    instanceIds.forEach((instanceId) => {
      const funcId = originalFunctionId(instanceId);
      const func = functionsMap[funcId];
      const version = runDocs[instanceId]?.function_version ?? null;
      if (!func || version === null || version === func.publishedVersion) return;
      const key = `${funcId}:${version}`;
      if (key in historicalBlockNames) return;
      loadVersionBlockNames(companyName, funcId, version)
        .then((names) => setHistoricalBlockNames((prev) => (key in prev ? prev : { ...prev, [key]: names })))
        .catch((err) => console.error(`Failed to read the block names of version ${version} of ${funcId}:`, err));
    });
  }, [companyName, instanceIds, functionsMap, runDocs, historicalBlockNames, originalFunctionId]);

  // The names to label a run's tabs with: those of the version it pinned. A run of
  // the version published now reads the loaded contract; an older run's names come
  // from the map above — null until read, or when gone, and the tabs then fall back
  // to their entry keys rather than to the current names.
  const blockNamesForRun = useCallback((funcId, func, run) => {
    const version = run?.function_version ?? null;
    if (version === null || version === func.publishedVersion) return versionBlockNames(func.versionContent);
    return historicalBlockNames[`${funcId}:${version}`] ?? null;
  }, [historicalBlockNames]);

  // What each widget needs to render: the run on screen for its instance, that
  // run's composite, and whether anything is still arriving.
  const instanceStates = useMemo(() => {
    const states = {};
    instanceIds.forEach((instanceId) => {
      const funcId = originalFunctionId(instanceId);
      const func = functionsMap[funcId] || functions?.find(candidate => candidate.id === funcId);
      if (!func) {
        states[instanceId] = null;
        return;
      }
      const result = instanceResults[instanceId];
      const hydration = (
        resultHydrations[instanceId]?.runId === result?.runId
          ? resultHydrations[instanceId]
          : null
      );
      // The run document and the result are one record keyed by run id. While a
      // re-run's document is still on its way, the slot holds the PREVIOUS run's
      // document: shown, it would put the old run's time, origin, version and
      // parameters under the new run's output — so the card gets no document,
      // and says its details are pending (or that they could not be loaded).
      const runDoc = runDocs[instanceId] || null;
      const runMatches = Boolean(runDoc && result?.runId && runDoc.id === result.runId);
      const run = runMatches ? runDoc : null;
      const awaitingRunDoc = Boolean(result?.runId) && !runMatches;
      states[instanceId] = {
        functionId: funcId,
        functionName: func.name || func.id,
        // Carried so a whole-composite card can label its `data` tabs by the
        // block that produced them rather than by the entry key — the names of
        // the version the shown run pinned, not of the one published now.
        blockNames: blockNamesForRun(funcId, func, run),
        run,
        runPending: awaitingRunDoc && !runDocErrors[instanceId],
        runDocError: awaitingRunDoc && Boolean(runDocErrors[instanceId]),
        onRetryRunDoc: () => setRunDocAttempt((n) => n + 1),
        chartSettings: headChartSettings[funcId] ?? null,
        hasUnpublishedChanges: unpublishedHeads[funcId] === true,
        outputs: result?.outputs || {},
        outputsSummary: result?.outputsSummary || null,
        fullLoaded: result?.fullLoaded === true,
        historySaveUnconfirmed: result?.historySaveUnconfirmed === true,
        loading: (
          Boolean(isLoading[instanceId])
          || Boolean(preparing[instanceId])
          || hydration?.status === 'loading'
          || Boolean(hydrating[instanceId])
        ),
        // A run request is in flight (or queued behind Run All) for this card.
        running: Boolean(isLoading[instanceId] || preparing[instanceId]),
        error: errors[instanceId] || '',
        versionNotice: versionNotices[instanceId] || null,
        onDismissVersionNotice: () => dismissVersionNotice(instanceId),
        hydrationError: hydration?.status === 'error' ? hydration.error : '',
        onRetryResult: hydration?.status === 'error'
          ? () => handleRetryStoredResult(instanceId)
          : null,
        truncatedTables: result?.truncatedTables || [],
      };
    });
    return states;
  }, [blockNamesForRun, 
    instanceIds,
    originalFunctionId,
    functionsMap,
    functions,
    instanceResults,
    runDocs,
    runDocErrors,
    unpublishedHeads,
    headChartSettings,
    isLoading,
    preparing,
    resultHydrations,
    hydrating,
    errors,
    versionNotices,
    dismissVersionNotice,
    handleRetryStoredResult,
  ]);

  const editorWidget = editorWidgetId ? widgets[editorWidgetId] : null;

  // If no view data, show placeholder
  if (!viewData || !functions) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Select a view from the sidebar or create a new one.
        </Typography>
      </Box>
    );
  }

  const truncatedNotices = Array.from(new Set(
    Object.values(instanceResults).flatMap((result) => result?.truncatedTables || [])
  ));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Grid layout with two columns - main content and sidebar */}
      <Grid
        container
        spacing={0}
        sx={{
          flexGrow: 1,
          height: '100%', // Take full height
          m: 0,
          width: '100%',
        }}
      >
        {/* Main dashboard grid with widgets */}
        <Grid item xs={12} md={9} lg="auto" sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexGrow: 1,
          flexBasis: 0,
          width: { lg: `calc(100% - ${paramsWidth})` },
          maxWidth: { lg: `calc(100% - ${paramsWidth})` },
          transition: 'all 0.3s ease',
        }}>
          <Box
            sx={{
              p: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* The view's name — the grid is otherwise anonymous — with its
                description beside it, the one place it is read outside the
                builder. */}
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flexShrink: 0 }}>
                {viewData.name || viewData.id}
              </Typography>
              {viewData.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={viewData.description}
                  sx={{ minWidth: 0 }}
                >
                  {`— ${viewData.description}`}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              Changes here are temporary. Use Edit View to save layout and display changes.
            </Typography>
            {viewChanged && (
              <Alert severity="info" sx={{ mb: 1 }} action={<Button onClick={onReload}>Reload View</Button>}>
                {latestView ? 'This View changed elsewhere. Reload to see the saved version.' : 'This View was deleted elsewhere.'}
              </Alert>
            )}
            {loadError && (
              <LoadFailure thing="this view" onRetry={() => setLoadAttempt((n) => n + 1)} align="left" sx={{ p: 0, mb: 1 }} />
            )}
            {streamError && (
              <LoadFailure thing="live run updates" onRetry={() => { setStreamAttempt(n => n + 1); setLoadAttempt(n => n + 1); }} align="left" sx={{ p: 0, mb: 1 }} />
            )}
            {truncatedNotices.length > 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {truncatedNotices.join(', ')} returned more than 50,000 rows - computed from the first 50,000 rows only.
              </Alert>
            )}

            {/* Content area */}
            <Box
              sx={{
                flexGrow: 1,
                width: '100%',
                position: 'relative',
                overflow: 'auto',
                p: 0,
              }}
            >
              {layouts.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                  This view has no cards yet. Edit the view to add some.
                </Typography>
              ) : (
                <AutoWidthGrid
                  className="layout"
                  layout={layouts}
                  cols={GRID_COLS}
                  rowHeight={GRID_ROW_HEIGHT}
                  // Widgets move and resize directly on the live view, but only
                  // from their own affordances — a grip in the title row and the
                  // corner handle — so scrolling and clicking inside a card are
                  // never mistaken for a drag.
                  isDraggable
                  isResizable
                  draggableHandle=".widget-drag-handle"
                  // Belt-and-braces alongside the scoped handle: react-grid-layout
                  // swallows mousedown inside a draggable region, so a control
                  // that ever ends up there would silently stop working.
                  draggableCancel=".MuiIconButton-root,button"
                  resizeHandles={['se']}
                  isBounded={false}
                  compactType="vertical"
                  preventCollision={false}
                  margin={[GRID_MARGIN, GRID_MARGIN]}
                  onDragStop={handleLayoutCommit}
                  onResizeStop={handleLayoutCommit}
                >
                  {layouts.map((layout) => {
                    const widget = widgets[layout.i];
                    if (!widget) return <div key={layout.i} />;
                    return (
                      <div key={layout.i}>
                        <WidgetCard
                          widgetId={layout.i}
                          widget={widget}
                          instanceState={widget.instanceId ? instanceStates[widget.instanceId] : null}
                          companyName={companyName}
                          onConfigure={setEditorWidgetId}
                          draggable
                        />
                      </div>
                    );
                  })}
                </AutoWidthGrid>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Right Parameters Sidebar - Match Dashboard.js style */}
        <Grid item xs={12} md={3} lg="auto" sx={{
          height: '100%',
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          width: { md: paramsCollapsed ? '56px' : '25%', lg: paramsWidth },
          maxWidth: { md: paramsCollapsed ? '56px' : '25%', lg: paramsWidth },
          flexBasis: { md: paramsCollapsed ? '56px' : '25%', lg: paramsWidth },
          ml: 'auto', // Push to the right
        }}>
          {paramsCollapsed && (
            <Box sx={{ pt: 1.5, display: 'flex', justifyContent: 'center' }}>
              <Tooltip title="Expand parameters" placement="left">
                <IconButton size="small" onClick={() => setParamsCollapsed(false)}>
                  <KeyboardDoubleArrowLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box sx={{
            p: 2,
            pr: 2,
            height: '100%',
            display: paramsCollapsed ? 'none' : 'block',
          }}>
            <Paper sx={{
              p: 2,
              height: '100%',
              borderRadius: 1.5,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 1,
              transition: 'all 0.2s ease',
              mr: 1,
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'text.primary' }}>
                  Parameters
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={unifyParameters
                    ? 'One control per column, applied to every card that uses it'
                    : 'Each card edited on its own'}>
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0 }}
                      control={(
                        <Switch
                          size="small"
                          checked={unifyParameters}
                          disabled={isHydrating}
                          onChange={(e) => setUnifyParameters(e.target.checked)}
                          inputProps={{ 'aria-label': 'Unify parameters across cards' }}
                        />
                      )}
                      label={<Typography variant="caption">Unify</Typography>}
                    />
                  </Tooltip>
                  <Tooltip title="Collapse parameters">
                    <IconButton size="small" onClick={() => setParamsCollapsed(true)}>
                      <KeyboardDoubleArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {lastRefreshedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {`Last refreshed ${formatRunTime(lastRefreshedAt)}${hasEnabledAutomation ? ' · auto-refresh on' : ''}`}
                </Typography>
              )}

              <KeepViewCurrentPanel
                companyName={companyName}
                viewId={viewData.id}
                viewName={viewData.name}
                cards={viewCards}
                instanceIds={instanceIds}
                ready={contractsReady}
                tables={viewTables}
                actorUid={currentUser?.uid || 'unknown'}
                onAutomationChange={handleAutomationChange}
                viewChanged={viewChanged}
              />

              <Box key={parameterClearGeneration} sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
                {unifyParameters ? (
                  <DateDraftScope {...dateScopeProps(UNIFIED_DATE_SCOPE)}>
                    {/* Merging the controls must not merge away a card's own
                        problem, so instance errors are named here instead. */}
                    {Object.entries(errors)
                      .filter(([, message]) => message)
                      .map(([instanceId, message]) => (
                        <Alert key={instanceId} severity="warning" sx={{ mb: 1.5 }}>
                          {`${functionsMap[originalFunctionId(instanceId)]?.name || instanceId}: ${message}`}
                        </Alert>
                      ))}

                    {unifiedParameters.map((param) => (
                      <ParameterField
                        key={param.name}
                        name={param.name}
                        error={param.targets.map(({ instanceId, blockId }) => (
                          preparedParameters[instanceId]?.errors.find((issue) => issue.blockId === blockId && issue.column === param.name)?.message
                        )).find(Boolean) || ''}
                        value={param.value}
                        disabled={param.targets.some(({ instanceId }) => hydrating[instanceId])}
                        onChange={(next) => handleUnifiedParameterChange(param, next)}
                        type={param.type}
                        isRange={param.isRange}
                        isDateRange={param.isDateRange}
                        isAttribute={param.isAttribute}
                        availableValues={attributeValues[param.name]}
                        indexedTableIds={indexedTableIdsFor(param.name, param.type, param.tableIds)}
                        labelVariant="caption"
                        excluded={isUnifiedExcluded(param)}
                        onToggleExclude={() => handleUnifiedToggleExclude(param)}
                      />
                    ))}

                    {unifiedParameters.length === 0 && unmergeableColumns.size === 0 && differingColumns.size === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No configurable parameters available for the functions in this view.
                      </Typography>
                    )}
                    {perCardColumns.size > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Set per card: these columns have different definitions or values.
                        </Typography>
                        {renderInstanceControls(perCardColumns)}
                      </Box>
                    )}
                  </DateDraftScope>
                ) : (
                  <>
                    {renderInstanceControls(null)}

                    {runnableInstanceIds.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No configurable parameters available for the functions in this view.
                      </Typography>
                    )}
                  </>
                )}
              </Box>

              {/* Clear + Run All at the bottom */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleClearParameters}
                  disabled={isHydrating || !hasActiveParameters}
                  sx={{ flexShrink: 0 }}
                >
                  Clear
                </Button>
                <Tooltip title={instanceIds.length === 0 ? 'Add a Function card to run this View' : ''}>
                  <Box component="span" sx={{ flex: 1, display: 'flex' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrowIcon />}
                      onClick={handleRunAll}
                      disabled={instanceIds.length === 0 || Object.values(isLoading).some(loading => loading) || Object.keys(preparing).length > 0 || Boolean(confirmState) || isHydrating}
                      fullWidth
                    >
                      Run All
                    </Button>
                  </Box>
                </Tooltip>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* The card display editor. It takes no view knowledge at all: the widget,
          the instance's current run, and a save callback. */}
      <WidgetDisplayEditor
        open={Boolean(editorWidget)}
        widgetId={editorWidgetId}
        widget={editorWidget}
        instanceState={editorWidget?.instanceId ? instanceStates[editorWidget.instanceId] : null}
        companyName={companyName}
        onClose={() => setEditorWidgetId(null)}
        onSave={handleWidgetSave}
        localOnly
      />

      {/* One effect confirmation before an effectful individual run, or ONE
          composite confirmation (grouped by function/instance) for Run All. */}
      <Dialog
        open={Boolean(confirmState)}
        onClose={confirmState?.busy ? undefined : handleCancelRun}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmState?.mode === 'all'
            ? 'Send emails and run selected functions'
            : 'Send email and run function'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {confirmState?.mode === 'all'
              ? 'One or more functions in this view send emails when they run. Please confirm all outbound emails below before they run.'
              : 'This function sends one or more emails when it runs. Please confirm the details below before it runs.'}
          </Typography>
          {confirmState?.error && <Alert severity="error" sx={{ mb: 2 }}>{confirmState.error}</Alert>}
          {confirmState?.changed && <Alert severity="warning" sx={{ mb: 2 }}>
            The recipients changed. Review the updated details before confirming again.
          </Alert>}
          {(confirmState?.groups || []).map((group) => (
            <Box key={group.instanceId} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {group.functionName}{group.version ? ` · version ${group.version}` : ''}
              </Typography>
              {group.outputs.map((output) => (
                <Box
                  key={output.blockId}
                  sx={{ mb: 1.5, p: 1.5, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{output.nodeName}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    <strong>To:</strong> {output.recipients.map((recipient) => recipient.label).join(', ') || '—'}
                  </Typography>
                  {output.recipients.filter(recipient => recipient.blocked).map(recipient => (
                    <Alert key={recipient.id} severity="warning" sx={{ my: 1 }}>
                      {recipient.label}: {recipient.blocked}
                    </Alert>
                  ))}
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    <strong>Subject:</strong> {output.subject || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    <strong>Attachments:</strong> {output.attachments.join(', ') || '—'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
          <Alert severity="info">
            Emails are sent to external recipients. Acceptance by a recipient&apos;s mail server does not guarantee inbox placement.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRun} disabled={confirmState?.busy}>Cancel</Button>
          <Button onClick={handleConfirmRun} variant="contained" disabled={confirmState?.busy}>
            {confirmState?.busy ? 'Sending…'
              : confirmState?.groups.some(group => blockedRecipients(group).length > 0)
                ? 'Recheck recipients' : 'Confirm and run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ViewDisplay;
